import { Controller } from "@hotwired/stimulus"
import EditorJS from "@editorjs/editorjs"
import Header from "@editorjs/header"
import Paragraph from "@editorjs/paragraph"
import Table from "@editorjs/table"
import ImageTool from "@editorjs/image"
import EmbedTool from "../editor_tools/embed_tool"
import ListTool from "../editor_tools/list_tool"
import CalloutTool from "../editor_tools/callout_tool"
import CalloutStatTool from "../editor_tools/callout_stat_tool"
import CardTool from "../editor_tools/card_tool"
import CardListTool from "../editor_tools/card_list_tool"
import CardZoneTool from "../editor_tools/card_zone_tool"
import HighlightListTool from "../editor_tools/highlight_list_tool"
import BannerWithIconTool from "../editor_tools/banner_with_icon_tool"
import TextColorInlineTool from "../editor_tools/text_color_inline_tool"
import DividerTool from "../editor_tools/divider_tool"
import DeviceScenesTool from "../editor_tools/device_scenes_tool"

class BrandColorInlineTool extends TextColorInlineTool {
  static get shortcut() {
    return "CMD+SHIFT+1"
  }
}

class AccentColorInlineTool extends TextColorInlineTool {
  static get shortcut() {
    return "CMD+SHIFT+2"
  }
}

export default class extends Controller {
  static targets = ["holder", "input"]
  static formControllers = new WeakMap()
  static formSubmitHandlers = new WeakMap()

  connect() {
    this.form = this.element.closest("form")
    if (!this.form) return

    const sharedInlineToolbar = ["bold", "italic", "link", "brandColor", "accentColor"]

    const paragraphTool = {
      class: Paragraph,
      inlineToolbar: sharedInlineToolbar,
      config: {
        preserveBlank: true
      }
    }

    const headerTool = {
      class: Header,
      config: {
        levels: [3, 4],
        defaultLevel: 3
      },
      inlineToolbar: sharedInlineToolbar
    }

    const listTool = {
      class: ListTool,
      inlineToolbar: sharedInlineToolbar
    }

    const tableTool = {
      class: Table,
      inlineToolbar: sharedInlineToolbar
    }

    const brandColorTool = {
      class: BrandColorInlineTool,
      config: {
        color: "#5B97D7",
        className: "article-text-brand",
        label: "A",
        title: "Brand text color"
      }
    }

    const accentColorTool = {
      class: AccentColorInlineTool,
      config: {
        color: "#d0dd47",
        className: "article-text-accent",
        label: "A",
        title: "Accent text color"
      }
    }

    const calloutTool = {
      class: CalloutTool,
      inlineToolbar: sharedInlineToolbar
    }

    const calloutStatTool = {
      class: CalloutStatTool,
      inlineToolbar: sharedInlineToolbar
    }

    const cardTool = {
      class: CardTool,
      inlineToolbar: sharedInlineToolbar
    }

    const cardListTool = {
      class: CardListTool,
      inlineToolbar: sharedInlineToolbar
    }

    const cardZoneTool = {
      class: CardZoneTool,
      inlineToolbar: sharedInlineToolbar
    }

    const highlightListTool = {
      class: HighlightListTool,
      inlineToolbar: sharedInlineToolbar
    }

    const bannerWithIconTool = {
      class: BannerWithIconTool,
      inlineToolbar: sharedInlineToolbar
    }

    const csrfToken = this.csrfToken()
    const imageTool = {
      class: ImageTool,
      config: {
        endpoints: {
          byFile: "/admin/article_images",
          byUrl: "/admin/article_images/fetch_url"
        },
        field: "image",
        additionalRequestHeaders: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        captionPlaceholder: "Image caption"
      }
    }

    this.editor = new EditorJS({
      holder: this.holderTarget,
      placeholder: "Write article content...",
      autofocus: true,
      defaultBlock: "paragraph",
      inlineToolbar: sharedInlineToolbar,
      data: this.parseInitialData(this.inputTarget.value),
      tools: {
        paragraph: paragraphTool,
        header: headerTool,
        list: listTool,
        table: tableTool,
        brandColor: brandColorTool,
        accentColor: accentColorTool,
        divider: DividerTool,
        card: cardTool,
        cardList: cardListTool,
        card_zone: cardZoneTool,
        highlight_list: highlightListTool,
        banner_with_icon: bannerWithIconTool,
        device_scenes: {
          class: DeviceScenesTool,
          inlineToolbar: false
        },
        image: imageTool,
        callout: calloutTool,
        calloutStat: calloutStatTool,
        embed: {
          class: EmbedTool,
          inlineToolbar: true
        }
      }
    })

    this.registerWithForm()
  }

  disconnect() {
    this.unregisterFromForm()

    if (this.editor && typeof this.editor.destroy === "function") {
      this.editor.destroy()
    }
  }

  static async handleFormSubmit(form, event) {
    event.preventDefault()
    event.stopImmediatePropagation()

    try {
      const controllers = this.formControllers.get(form) || new Set()
      for (const controller of controllers) {
        await controller.syncInput()
      }
      // Use prototype so a child control named "submit" cannot shadow the method.
      HTMLFormElement.prototype.submit.call(form)
    } catch (_error) {
      window.alert("Could not save article body. Please try again.")
    }
  }

  registerWithForm() {
    const controllers = this.constructor.formControllers.get(this.form) || new Set()
    controllers.add(this)
    this.constructor.formControllers.set(this.form, controllers)

    if (!this.constructor.formSubmitHandlers.has(this.form)) {
      const submitHandler = this.constructor.handleFormSubmit.bind(this.constructor, this.form)
      this.form.addEventListener("submit", submitHandler, true)
      this.constructor.formSubmitHandlers.set(this.form, submitHandler)
    }
  }

  unregisterFromForm() {
    if (!this.form) return

    const controllers = this.constructor.formControllers.get(this.form)
    if (controllers) {
      controllers.delete(this)
      if (controllers.size === 0) {
        this.constructor.formControllers.delete(this.form)

        const submitHandler = this.constructor.formSubmitHandlers.get(this.form)
        if (submitHandler) {
          this.form.removeEventListener("submit", submitHandler, true)
          this.constructor.formSubmitHandlers.delete(this.form)
        }
      }
    }
  }

  async syncInput() {
    if (!this.editor) return
    if (this.editor.isReady) {
      await this.editor.isReady
    }

    const output = await this.editor.save()
    this.inputTarget.value = JSON.stringify(output)
  }

  parseInitialData(rawValue) {
    const value = (rawValue || "").trim()
    if (!value) {
      return { blocks: [] }
    }

    try {
      const parsed = JSON.parse(value)
      if (parsed && Array.isArray(parsed.blocks)) {
        return parsed
      }
    } catch (_error) {
      // Fall through for legacy plain-text content.
    }

    return {
      blocks: value
        .split(/\n{2,}/)
        .map((chunk) => chunk.trim())
        .filter((chunk) => chunk.length > 0)
        .map((chunk) => ({ type: "paragraph", data: { text: chunk } }))
    }
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
